import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import FilteredTableCore from "../../../components/Organismos/Table/FilteredTableCore";
import {
  CUSTOM_FILTER_CHIP_ID,
  type IFilterData,
  type IHeader,
} from "../../../components/Organismos/Table/FilteredTable.types";
import Button from "../../../components/staff/Button";
import Stats from "../../../components/Atomos/Icons/Stats";
import { getEmailCategories } from "../../../Services/EmailCategory";
import { getEmailTemplatesIncludeDeleted } from "../../../Services/EmailTemplate";
import {
  formatEventLookupLabel,
  getEventLookupOptions,
} from "../../../Services/Event";
import type { IEmailCategory } from "../../../interfaces/models/IEmailCategory";
import type { IEmailLog } from "../../../interfaces/models/IEmailLog";
import { formatTimestampDate } from "../../../utils/dates";
import TemplateDateFlightsPicker from "../../SendEmail/TemplateDateFlightsPicker";
import {
  buildTemplateCreatedDateFilter,
  summarizeTemplateDateFilterSelection,
} from "../../SendEmail/templateDateFilter";
import {
  DEFAULT_TEMPLATE_DATE_FILTER,
  type TemplateDateFilterValue,
} from "../../../utils/templateDateFilterTypes";
import EmailStatsDashboard from "./EmailStatsDashboard";
import EmailStatsStatusBadge from "./EmailStatsStatusBadge";
import { LogStatusEnum, SendStatusEnum } from "../../../Enums/EmailEnum";

const endpoint = "/emailLog";

const ACTIVITY_FILTER_OPTIONS = [
  { id: "opened", name: "Opened" },
  { id: "clicked", name: "Clicked" },
  { id: "bounced", name: "Bounced" },
  { id: "spam", name: "Spam" },
];

function yesNoFromTimestamp (at: unknown): string {
  return at != null && Number(at) > 0 ? "Yes" : "No";
}

function yesNoFromDeliveryStatus (
  deliveryStatus: unknown,
  status: string,
  at?: unknown,
): string {
  if (String(deliveryStatus || "") === status) {
    return "Yes";
  }
  return yesNoFromTimestamp(at);
}

function templateDateFlightFilterChips (filterValue: unknown) {
  const label = summarizeTemplateDateFilterSelection(
    filterValue as TemplateDateFilterValue | null | undefined,
  );
  if (!label) {
    return [];
  }
  return [{ id: CUSTOM_FILTER_CHIP_ID, name: label }];
}

export default function EmailStatsPage (): React.ReactElement {
  const [showStats, setShowStats] = useState(false);
  const [emailCategories, setEmailCategories] = useState<IEmailCategory[]>([]);
  const [eventOptions, setEventOptions] = useState<
    { id: string; name: string }[]
  >([]);
  const [fromEmailOptions, setFromEmailOptions] = useState<
    { id: string; name: string }[]
  >([]);

  useEffect(() => {
    getEmailCategories()
      .then((data) => setEmailCategories(Array.isArray(data) ? data : []))
      .catch(() => setEmailCategories([]));
    getEventLookupOptions()
      .then((data) =>
        setEventOptions(
          (data || [])
            .filter((e) => e?.id)
            .map((e) => ({
              id: String(e.id),
              name: formatEventLookupLabel(e),
            })),
        ),
      )
      .catch(() => setEventOptions([]));
    getEmailTemplatesIncludeDeleted({ fields: ["fromEmail"] })
      .then((rows) => {
        const byLower = new Map<string, string>();
        for (const row of rows || []) {
          const email = String(row.fromEmail || "").trim();
          if (!email) {
            continue;
          }
          const key = email.toLowerCase();
          if (!byLower.has(key)) {
            byLower.set(key, email);
          }
        }
        setFromEmailOptions(
          [...byLower.values()]
            .sort((a, b) => a.localeCompare(b))
            .map((email) => ({ id: email, name: email })),
        );
      })
      .catch(() => setFromEmailOptions([]));
  }, []);

  const categoryFilterOptions = useMemo(
    () =>
      emailCategories.map((c) => ({
        id: c.id ?? c.name,
        name: c.name,
      })),
    [emailCategories],
  );

  const statusFilterOptions = useMemo(
    () =>
      Object.entries(LogStatusEnum).map(([id, name]) => ({
        id,
        name,
      })),
    [],
  );

  const filtersData: IFilterData[] = useMemo(
    () => [
      {
        name: "Date",
        fieldName: "createdDate",
        type: "custom",
        defaultCustomFilterValue: DEFAULT_TEMPLATE_DATE_FILTER,
        getCustomSelectedChips: templateDateFlightFilterChips,
        render: ({ value, apply }) => (
          <TemplateDateFlightsPicker
            value={
              (value as TemplateDateFilterValue | null | undefined) ??
              DEFAULT_TEMPLATE_DATE_FILTER
            }
            onApply={(next) => {
              const built = buildTemplateCreatedDateFilter(
                next.templateDatePreset,
                next.templateDateFrom,
                next.templateDateTo,
              );
              apply(next, built?.createdDate ?? null);
            }}
            variant="catalog"
            label="By Created Date:"
            dialogTitle="Created date"
            showYearPresets={false}
            showLast3Days
            showLast7Days
            showLastWeek={false}
            labelClassName="font-semibold text-gray-font"
            triggerClassName="rounded-md border border-solid !border-gray-300 !bg-gray-25 !shadow-none !hover:shadow-md"
            triggerInsetClassName="max-h-[0.875rem]"
          />
        ),
      },
      {
        name: "Category",
        fieldName: "category",
        type: "multiselect",
        objectData: categoryFilterOptions,
        comparison: "BY_NAME",
      },
      {
        name: "Event",
        fieldName: "emailEventId",
        type: "multiselect",
        objectData: eventOptions,
        comparison: "EQUAL",
      },
      {
        name: "Delivery Status",
        fieldName: "sendStatus",
        type: "multiselect",
        objectData: statusFilterOptions,
        comparison: "EQUAL",
      },
      {
        name: "End Result",
        fieldName: "engagement",
        type: "multiselect",
        objectData: ACTIVITY_FILTER_OPTIONS,
        comparison: "IN",
        sortByName: false,
      },
      {
        name: "From Email",
        fieldName: "fromEmail",
        type: "multiselect",
        objectData: fromEmailOptions,
        comparison: "EQUAL",
      },
    ],
    [categoryFilterOptions, eventOptions, fromEmailOptions, statusFilterOptions],
  );

  const formatData = useCallback((rows: IEmailLog[] | any[]) => {
    if (!rows || !Array.isArray(rows)) {
      return [];
    }
    return rows.map((row) => ({
      ...row,
      createdDate: formatTimestampDate(row.createdDate) || "",
      contact: row.contact || "",
      firm: row.firm || "",
      sendStatus:
        SendStatusEnum[row.sendStatus as keyof typeof SendStatusEnum] || "",
      opened: yesNoFromTimestamp(row.openedAt),
      clicked: yesNoFromTimestamp(row.clickedAt),
      bounced: yesNoFromDeliveryStatus(
        row.deliveryStatus,
        "Bounced",
        row.bouncedAt,
      ),
      spam: yesNoFromDeliveryStatus(
        row.deliveryStatus,
        "FilteredSpam",
        row.filteredSpamAt,
      ),
    }));
  }, []);

  const columnsDef: IHeader[] = useMemo(
    () => [
      {
        id: "createdDate",
        header: "Created Date",
        width: 120,
      },
      {
        id: "subject",
        header: "Subject",
        width: 280,
        cell: ({ row }) => {
          const subject = String(row.subject || "").trim();
          const id = String(row.id || "").trim();
          if (!subject || !id) {
            return subject;
          }
          return (
            <Link
              to={`/email-stats/${id}`}
              className="text-secondary hover:underline font-medium"
            >
              {subject}
            </Link>
          );
        },
      },
      {
        id: "emailNumber",
        header: "Template ID",
        width: 180,
        cell: ({ row }) =>
          row.emailNumber != null && row.emailNumber !== ""
            ? String(row.emailNumber)
            : "",
      },
      {
        id: "recipientEmail",
        header: "Recipient Email",
        width: 220,
      },
      {
        id: "category",
        header: "Category",
        width: 100,
      },
      {
        id: "sendStatus",
        header: "Status",
        width: 100,
        cell: ({ row }) => <EmailStatsStatusBadge status={row.sendStatus} />,
      },
      {
        id: "contact",
        header: "Contact",
        width: 150,
      },
      {
        id: "firm",
        header: "Firm",
        width: 150,
      },
      {
        id: "opened",
        header: "Opened",
        width: 70,
      },
      {
        id: "clicked",
        header: "Clicked",
        width: 70,
      },
      {
        id: "bounced",
        header: "Bounced",
        width: 70,
      },
      {
        id: "spam",
        header: "Spam",
        width: 70,
      },
    ],
    [],
  );

  const statsBanner = useMemo(
    () => (showStats ? <EmailStatsDashboard /> : null),
    [showStats],
  );

  return (
    <FilteredTableCore
      columnsDef={columnsDef}
      showRowNumber
      endpoint={endpoint}
      filtersData={filtersData}
      formatData={formatData}
      // Bumped from the default pathname key: saved prefs still hold the retired
      // `sentAt` filter/sort, which would keep filtering rows with no visible chip.
      tableKey="email-stats-created-date"
      defaultSorting={{ field: "createdDate", order: "desc" }}
      defaultPageSize={50}
      showSearchInput
      searchPlaceholder="Search by Email, Contact, or Firm"
      showExcelExport
      quickFilterBarAccessory={
        <div className="flex flex-wrap items-center gap-2">
          <Button
            text={showStats ? "Hide Stats" : "Show Stats"}
            color="white"
            variant="outlined"
            size="fixed"
            onClick={() => setShowStats((v) => !v)}
            icon={<Stats />}
          />
        </div>
      }
      bannerContent={statsBanner}
    />
  );
}
